// components/FlexibleLineChart.jsx
import { useState } from 'react';
import { ResponsiveLine } from '@nivo/line';
import Tippy from '@tippyjs/react';
import { sticky } from 'tippy.js';
import { motion } from 'framer-motion';
import 'tippy.js/dist/tippy.css';
import { useChartExport } from '../hooks/useChartExport';
import ExportPopover from './ExportPopover';

// Color palette for multiple series
const DEFAULT_COLORS = ['#60A5FA', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6'];

// Helper: split text into lines for word wrapping
function splitTextOnWords(text, maxLength) {
  if (!text) return [];
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    if ((currentLine + ' ' + word).trim().length > maxLength) {
      if (currentLine) lines.push(currentLine.trim());
      currentLine = word;
    } else {
      currentLine += (currentLine ? ' ' : '') + word;
    }
  });
  if (currentLine) lines.push(currentLine.trim());
  return lines;
}

// Helper: format values for display
const formatValue = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'number') {
    if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toFixed(1);
  }
  return String(value);
};

// Chart theme
const CHART_THEME = {
  fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
  background: '#ffffff',
  grid: {
    line: {
      stroke: '#e5e7eb',
      strokeWidth: 1,
    },
  },
  axis: {
    domain: {
      line: {
        stroke: '#777777',
        strokeWidth: 1,
      },
    },
    ticks: {
      line: {
        stroke: '#777777',
        strokeWidth: 1,
      },
      text: {
        fill: '#333333',
        fontSize: '0.875rem',
        fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
      },
    },
    legend: {
      text: {
        fill: '#333333',
        fontSize: '1rem',
        fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
      },
    },
  },
  legends: {
    text: {
      fill: '#333333',
      fontSize: '0.875rem',
      fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
    },
  },
  crosshair: {
    line: {
      stroke: '#374151',
      strokeWidth: 1,
      strokeDasharray: '6 6',
    },
  },
};

export default function FlexibleLineChart({
  data,
  title,
  xAxisLabel,
  yAxisLabel,
  curve = 'monotoneX',
  enableArea = false,
  colors = DEFAULT_COLORS,
  showPoints = true,
  showLegend = true,
  yScaleMin = 0,
  yScaleMax = 'auto',
}) {
  const [hoveredX, setHoveredX] = useState(null);
  const [tooltipContent, setTooltipContent] = useState(null);

  // Prepare data for clipboard export
  const prepareDataForClipboard = (chartData) => {
    const lines = [];
    if (title) lines.push(`Title: ${title}`);
    // Header row with x values
    chartData.forEach((series) => {
      lines.push(`\n${series.id}`);
      lines.push('X\tY');
      series.data.forEach((pt) => {
        lines.push(`${pt.x}\t${pt.y ?? ''}`);
      });
    });
    return lines.join('\n');
  };

  // Use the chart export hook
  const {
    chartRef,
    exportChartAsPNG,
    exportChartToExcel,
    copyImageToClipboard,
    copyDataToClipboard,
  } = useChartExport({
    defaultFileName: title ? title.replace(/\s+/g, '_').toLowerCase() : 'line_chart',
    chartData: data,
    prepareDataForClipboard,
  });

  // Title layer with word wrapping
  const titleLayer = ({ width, margin }) => {
    if (!title) return null;

    const chartWidth = width - margin.left - margin.right;
    const maxChars = Math.floor(chartWidth / 9);
    const lines = splitTextOnWords(title, maxChars);
    const fontSize = lines.length > 2 ? '0.95rem' : '1.15rem';
    const xCenter = chartWidth / 2;
    const yPos = -margin.top + 25;

    return (
      <g>
        <text
          x={xCenter}
          y={yPos}
          textAnchor="middle"
          style={{
            fontSize,
            fontWeight: 'bold',
            fill: '#333333',
            fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
          }}
        >
          {lines.map((line, i) => (
            <tspan key={i} x={xCenter} dy={i === 0 ? 0 : '1.3em'}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  };

  // Custom points layer with colored circles
  const customPointsLayer = ({ points }) => {
    if (!showPoints) return null;

    return (
      <g>
        {points.map((pt) => (
          <circle
            key={pt.id}
            cx={pt.x}
            cy={pt.y}
            r={5}
            fill="#ffffff"
            stroke={pt.serieColor}
            strokeWidth={2.5}
            style={{ pointerEvents: 'none' }}
          />
        ))}
      </g>
    );
  };

  // Custom slice layer with animated crosshair
  const crosshairSliceLayer = ({ slices, height, xScale, margin }) => {
    return (
      <g>
        {slices.map((slice) => {
          const firstPoint = slice.points[0];
          const xCoord = xScale(firstPoint.data.x);

          // Build tooltip content for all series at this x
          const content = (
            <div className="text-slate-700 rounded-md px-3 py-2 bg-zinc-50 outline outline-zinc-100 outline-4 border-white border-2 -outline-offset-1">
              <div className="font-semibold text-slate-800 mb-2 border-b border-zinc-100 pb-1">
                {firstPoint.data.xFormatted || firstPoint.data.x}
              </div>
              {slice.points.map((point) => (
                <div key={point.id} className="flex items-center gap-2 py-0.5">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: point.serieColor }}
                  />
                  <span className="font-medium">{point.serieId}:</span>
                  <span>{formatValue(point.data.y)}</span>
                </div>
              ))}
            </div>
          );

          return (
            <rect
              key={slice.id}
              x={slice.x0}
              y={slice.y0}
              width={slice.width}
              height={slice.height}
              fill="transparent"
              onMouseEnter={() => {
                setHoveredX(margin.left + xCoord);
                setTooltipContent(content);
              }}
              onMouseLeave={() => {
                setHoveredX(null);
                setTooltipContent(null);
              }}
            />
          );
        })}

        {/* Animated crosshair line */}
        {hoveredX !== null && (
          <motion.line
            style={{ pointerEvents: 'none' }}
            y1={0}
            y2={height}
            stroke="#374151"
            strokeWidth={1.5}
            strokeDasharray="5 5"
            initial={{
              x1: hoveredX - margin.left,
              x2: hoveredX - margin.left,
              opacity: 0,
            }}
            animate={{
              x1: hoveredX - margin.left,
              x2: hoveredX - margin.left,
              opacity: 1,
            }}
            transition={{
              type: 'spring',
              stiffness: 200,
              damping: 30,
            }}
          />
        )}
      </g>
    );
  };

  // Determine margins based on title and legend
  const topMargin = title ? 80 : 50;
  const margin = { top: topMargin + (showLegend ? 30 : 0), right: 30, bottom: 60, left: 70 };

  // Build layers array
  const layers = [
    'grid',
    'markers',
    'areas',
    'lines',
    'axes',
    crosshairSliceLayer,
    customPointsLayer,
    'legends',
    titleLayer,
  ];

  return (
    <div>
      {/* Export controls */}
      <div className="flex justify-end mb-2">
        <ExportPopover
          onCopyImage={copyImageToClipboard}
          onCopyData={copyDataToClipboard}
          onExportPNG={exportChartAsPNG}
          onExportExcel={exportChartToExcel}
        />
      </div>
      <div ref={chartRef} className="w-full relative" style={{ height: 420 }}>
        <ResponsiveLine
        data={data}
        theme={CHART_THEME}
        margin={margin}
        xScale={{ type: 'point' }}
        yScale={{
          type: 'linear',
          min: yScaleMin,
          max: yScaleMax,
          stacked: false,
        }}
        curve={curve}
        enableArea={enableArea}
        areaBaselineValue={0}
        areaOpacity={0.1}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: xAxisLabel,
          legendOffset: 45,
          legendPosition: 'middle',
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: yAxisLabel,
          legendOffset: -50,
          legendPosition: 'middle',
        }}
        colors={colors}
        lineWidth={3}
        enablePoints={false} // We use custom points layer
        useMesh={false}
        enableSlices="x"
        layers={layers}
        legends={showLegend ? [
          {
            anchor: 'top',
            direction: 'row',
            justify: false,
            translateX: 0,
            translateY: -30,
            itemsSpacing: 20,
            // Dynamic width based on longest series name (~8px per char + symbol)
            itemWidth: Math.max(80, Math.max(...data.map(s => s.id?.length || 0)) * 8 + 30),
            itemHeight: 20,
            itemDirection: 'left-to-right',
            itemOpacity: 0.85,
            symbolSize: 12,
            symbolShape: 'circle',
          },
        ] : []}
      />

      {/* Floating tooltip positioned at crosshair */}
      {hoveredX !== null && tooltipContent && (
        <Tippy
          interactive={false}
          sticky={true}
          plugins={[sticky]}
          placement="top"
          offset={[0, 15]}
          content={tooltipContent}
          visible={true}
          arrow={true}
          theme="industryTrends"
        >
          <motion.div
            animate={{ left: hoveredX }}
            transition={{
              type: 'spring',
              stiffness: 180,
              damping: 40,
            }}
            style={{
              position: 'absolute',
              top: topMargin,
              left: hoveredX,
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
            }}
          />
        </Tippy>
      )}
      </div>
    </div>
  );
}
